import { DeleteOutlined } from '@ant-design/icons';
import { Table, Row, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldArray } from 'react-hook-form';
import {
  NumberOnlyField,
  TextAreaField,
  CheckBoxField,
} from 'src/components/form';
import { UseFormReturn } from 'react-hook-form';
import { FormValue } from '../useForm';
import ManuFacturingSize from '../ManuFacturingSize';
import SizeInput from '../SizeInput';
import ProductModal from '../ProductModal';
import MachiningMethodList from '../MachiningMethodList';
import { MILLSHEET_MULTIPLE_QUOTES_OPTIONS } from 'src/constants/quotation/optionSelect';
import { MIN_ITEMS_FOR_SCROLL } from '../constants';
import { checkIsProductFinished, renderWeight } from 'src/utils/helper';
import QuoteDetailCell from 'src/containers/MultipleQuotes/QuoteDetailCell';
import { CATEGORIES_ID } from 'src/constants/app';

type Props = {
  formInfo: UseFormReturn<FormValue, any>;
  setEstimateErrors: (e) => void;
  setStockError: (e) => void;
  dataReEstimate?: Response.QuotationItem[];
  prevLang: string;
  lang: string;
  resetInputData: (index: number) => void;
  estimateErrors: string[];
  stockError: string;
  handleDelete: (index: number) => void;
  setDataReEstimate: (data: Response.QuotationItem[]) => void;
  fractionsData?: Response.Fractions[];
  isAdding: boolean;
  setIsAdding: (isAdding: boolean) => void;
};

const MainTable: React.FC<Props> = ({
  formInfo,
  setEstimateErrors,
  setStockError,
  dataReEstimate = [],
  prevLang,
  lang,
  resetInputData,
  handleDelete,
  setDataReEstimate,
  fractionsData,
  isAdding,
  setIsAdding,
}) => {
  const tableRef = useRef<HTMLDivElement>(null);

  const { control, setValue, getValues, watch } = formInfo;
  const { t } = useTranslation();

  const { remove } = useFieldArray({
    control,
    name: 'priceQuotes',
    keyName: 'id', // AntD will use this for rowKey
  });

  // Set initial values from dataReEstimate
  useEffect(() => {
    if (Array.isArray(dataReEstimate) && dataReEstimate.length > 0) {
      dataReEstimate.forEach((item, index) => {
        !getValues(`priceQuotes.${index}.number_product`) &&
          setValue(
            `priceQuotes.${index}.number_product`,
            item.inzu?.toString() || '',
          );
        !getValues(`priceQuotes.${index}.order_notes`) &&
          setValue(
            `priceQuotes.${index}.order_notes`,
            item.biko?.toString() || '',
          );
        !getValues(`priceQuotes.${index}.mill_sheets`) &&
          setValue(
            `priceQuotes.${index}.mill_sheets`,
            item.optc091?.toString() || '',
          );
        !getValues(`priceQuotes.${index}.variety`) &&
          setValue(
            `priceQuotes.${index}.variety`,
            item.hinshL3Cd?.toString() || '',
          );
        !getValues(`priceQuotes.${index}.shape`) &&
          setValue(
            `priceQuotes.${index}.shape`,
            item.keijoL3Cd?.toString() || '',
          );
        !getValues(`priceQuotes.${index}.product`) &&
          setValue(
            `priceQuotes.${index}.product`,
            item.product?.toString() || '',
          );
        !getValues(`priceQuotes.${index}.isProductFinishedFlag`) &&
          setValue(
            `priceQuotes.${index}.isProductFinishedFlag`,
            checkIsProductFinished(item?.hinshL3Cd?.toString()),
          );
      });
    }
  }, [dataReEstimate, setValue]);

  // Handler for mill_sheets checkbox
  const handleMillSheetChange = useCallback(
    (e, index) => {
      setValue(
        `priceQuotes.${index}.mill_sheets`,
        e.target.checked ? MILLSHEET_MULTIPLE_QUOTES_OPTIONS(t)[1].value : '',
      );
    },
    [setValue, t],
  );

  // Helper: mill sheet label/checked status
  const getMillSheetProps = (index: number) => {
    const checked =
      watch(`priceQuotes.${index}.mill_sheets`) ===
      MILLSHEET_MULTIPLE_QUOTES_OPTIONS(t)[1].value;
    const label = (
      <span className="mill-sheets_label">
        {MILLSHEET_MULTIPLE_QUOTES_OPTIONS(t)[1].label}
      </span>
    );
    return { label, checked };
  };

  const handleRemoveReEstimateRow = (record) => {
    const idx = dataReEstimate.findIndex((item) => item.id === record?.id);
    if (!record?.detail && idx !== -1) {
      remove(idx); // sync form array at index
      handleDelete(idx); // sync dataReEstimate at index
    }
  };

  // Columns
  const columns: ColumnsType<Response.QuotationItem> = [
    {
      title: t('multiple_quotes_page.product_name'),
      dataIndex: 'productName',
      width: 120,
      className: 'td-product-name',
      render: (_: string, record: Response.QuotationItem, index: number) => (
        <ProductModal
          index={index}
          formInfo={formInfo}
          dataReEstimates={dataReEstimate}
          dataReEstimate={record}
          setDataReEstimate={setDataReEstimate}
          resetInputData={resetInputData}
        />
      ),
    },
    {
      title: t('multiple_quotes_page.processing_method'),
      dataIndex: 'processingMethod',
      width: 170,
      render: (_: string, record: Response.QuotationItem, index: number) => (
        <MachiningMethodList
          index={index}
          formInfo={formInfo}
          dataReEstimate={record}
          prevLang={prevLang}
          lang={lang}
        />
      ),
    },
    {
      title: t('multiple_quotes_page.dimensions'),
      dataIndex: 'dimensions',
      width: 420,
      className: 'td-product-size',
      render: (_: string, record: Response.QuotationItem, index: number) => {
        const { label, checked } = getMillSheetProps(index);
        return (
          <>
            <ManuFacturingSize
              index={index}
              formInfo={formInfo}
              setEstimateErrors={setEstimateErrors}
              setStockError={setStockError}
              dataReEstimate={record}
              fractionsData={fractionsData}
            />
            <SizeInput
              index={index}
              formInfo={formInfo}
              dataReEstimate={record}
            />
            {record?.product &&
              record?.hinshL3Cd !== CATEGORIES_ID.TOOL_PRODUCTS && (
                <div className="mill-sheets_wrap">
                  <p className="mill-sheets_title">
                    {t('multiple_quotes_page.mill_sheet')}:
                  </p>
                  <CheckBoxField
                    label={label}
                    controller={{
                      control,
                      name: `priceQuotes.${index}.mill_sheets`,
                    }}
                    checkboxProps={{
                      checked,
                      disabled: checkIsProductFinished(
                        getValues(`priceQuotes.${index}.variety`),
                      ),
                      onChange: (e) => handleMillSheetChange(e, index),
                    }}
                  />
                </div>
              )}
          </>
        );
      },
    },
    {
      title: (
        <div>
          {t('multiple_quotes_page.quantity')}
          <br />
          {dataReEstimate?.some((item) => item?.detail) && (
            <>
              <span>/ </span>
              {t('multiple_quotes_page.weight')}
            </>
          )}
        </div>
      ),
      dataIndex: 'quantity',
      width: 120,
      render: (_: string, _row, index: number) => (
        <Row align="middle" className="quantity-wrap">
          <NumberOnlyField<FormValue>
            controller={{
              control,
              name: `priceQuotes.${index}.number_product`,
            }}
            showErrorTooltip
            inputProps={{ maxLength: 9 }}
            isOnlyInteger
            className="size_input"
          />
          {renderWeight(_row?.detail?.henkanJury, _row?.detail?.marumeJury) && (
            <div>
              <p>{t('multiple_quotes_page.weight')}</p>
              <p>
                {renderWeight(
                  _row?.detail?.henkanJury,
                  _row?.detail?.marumeJury,
                )}
              </p>
            </div>
          )}
        </Row>
      ),
    },
    {
      title: (
        <div>
          {t('multiple_quotes_page.remarks')}
          <br />
          <span className="title-sub">
            {t('multiple_quotes_page.remarks_sub_title')}
          </span>
        </div>
      ),
      dataIndex: 'address',
      width: 160,
      render: (_: string, _row, index: number) => (
        <TextAreaField
          controller={{
            control,
            name: `priceQuotes.${index}.order_notes`,
          }}
          textAreaProps={{
            maxLength: 80,
            autoSize: { minRows: 1, maxRows: 6 },
          }}
          showErrorTooltip
        />
      ),
    },
    {
      title: t('multiple_quotes_page.quotation_result'),
      dataIndex: 'remarks',
      width: 380,
      className: 'td-remarks',
      render: (_: string, record: Response.QuotationItem) =>
        record?.detail && <QuoteDetailCell detail={record.detail} t={t} />,
    },
    {
      title: '',
      dataIndex: 'action',
      width: 35,
      className: 'td-action',
      render: (_: string, record: Response.QuotationItem) => {
        return (
          <Button
            icon={<DeleteOutlined />}
            className={`action_button ${!record?.detail && 'active'}`}
            onClick={() => handleRemoveReEstimateRow(record)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (isAdding) {
      const body = tableRef.current?.querySelector('.ant-table-body');
      if (body) {
        body.scrollTop = body.scrollHeight;
      }
      setIsAdding(false);
    }
  }, [dataReEstimate, isAdding]);

  return (
    <div ref={tableRef}>
      <Table
        rowKey="id"
        className="container_table"
        columns={columns}
        dataSource={dataReEstimate}
        tableLayout="fixed"
        scroll={{
          x: 'max-content',
          y: dataReEstimate?.length > MIN_ITEMS_FOR_SCROLL ? 450 : 1000,
        }}
        pagination={false}
      />
    </div>
  );
};

export default MainTable;
